import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, ArrowUpRight, CheckCircle2, Zap, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { fetchTechTopic, fetchArticles, fetchServices } from "../lib/api";
import { Icon, SectionLoader } from "../components/shared";

export default function TechDetail() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [articles, setArticles] = useState([]);
  const [relatedServices, setRelatedServices] = useState([]);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    setStatus("loading");
    Promise.all([
      fetchTechTopic(slug),
      fetchArticles({ tech: slug }),
      fetchServices(),
    ])
      .then(([t, arts, services]) => {
        setTopic(t);
        setArticles(arts);
        if (t.related_service_slugs?.length) {
          setRelatedServices(
            services.filter((s) => t.related_service_slugs.includes(s.slug))
          );
        }
        setStatus("ok");
      })
      .catch(() => setStatus("notfound"));
  }, [slug]);

  if (status === "loading") {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[76px]"><SectionLoader /></div>
      </div>
    );
  }
  if (status === "notfound" || !topic) {
    return (
      <div className="min-h-screen nst-light">
        <Navbar variant="solid" />
        <div className="pt-[140px] px-6 text-center">
          <h1 className="text-3xl font-bold text-[#0A1F47]">Tech topic not found</h1>
          <Link to="/#tech" className="btn-navy mt-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#EDF1FB]">
      <Navbar variant="solid" />

      {/* Hero */}
      <section className="relative overflow-hidden nst-navy text-white pt-[140px] pb-24">
        <div className="absolute inset-0 bg-grid-dark opacity-30" />
        <div className="glow-orb w-[500px] h-[500px] -top-20 -right-20" style={{ background: "#2E6BF6" }} />
        <div className="glow-orb w-[400px] h-[400px] bottom-0 -left-20" style={{ background: "#16C47F" }} />

        <div className="relative max-w-[1280px] mx-auto px-6 lg:px-10">
          <Link to="/#tech" className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 hover:text-white mb-8">
            <ArrowLeft className="w-4 h-4" /> All Technologies
          </Link>

          <div className="flex items-center gap-5 mb-6">
            <div className="w-20 h-20 rounded-2xl bg-[#2E6BF6]/15 border border-[#2E6BF6]/30 flex items-center justify-center">
              <Icon name={topic.icon} className="w-9 h-9 text-[#6B9FFF]" />
            </div>
            <span className="pill-label on-dark">Technology</span>
          </div>

          <h1 className="text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.02]">
            {topic.name}
          </h1>
          <p className="mt-5 text-xl md:text-2xl text-[#6B9FFF] font-semibold max-w-3xl">
            {topic.tagline}
          </p>
          <p className="mt-5 text-base md:text-lg text-white/75 max-w-3xl leading-relaxed">
            {topic.long_desc || topic.short_desc}
          </p>

          {topic.tools?.length > 0 && (
            <div className="mt-8 flex flex-wrap gap-2.5">
              {topic.tools.map((tool) => (
                <span key={tool} className="tech-badge">{tool}</span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Capabilities */}
      {topic.capabilities?.length > 0 && (
        <section className="nst-light py-20">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <span className="pill-label"><Zap className="w-3.5 h-3.5" /> Capabilities</span>
            <h2 className="mt-6 text-3xl md:text-4xl font-extrabold text-[#0A1F47] leading-tight">
              What we build with <span className="text-[#2E6BF6]">{topic.name}</span>
            </h2>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
              {topic.capabilities.map((cap) => (
                <div key={cap} className="bg-white border border-[#E2E8F4] rounded-2xl p-6 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-[#EDF1FB] flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-[#2E6BF6]" />
                  </div>
                  <p className="text-[#0A1F47] font-medium">{cap}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sections */}
      {topic.sections?.length > 0 && (
        <section className="nst-light pb-20">
          <div className="max-w-[1000px] mx-auto px-6 lg:px-10 space-y-8">
            {topic.sections.map((sec, i) => (
              <div key={i} className="bg-white border border-[#E2E8F4] rounded-3xl p-8 md:p-10">
                {sec.heading && (
                  <h3 className="text-2xl md:text-3xl font-extrabold text-[#0A1F47]">{sec.heading}</h3>
                )}
                {sec.body && (
                  <p className="mt-4 text-[#0A1F47]/80 leading-relaxed text-[17px]">{sec.body}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Related services */}
      {relatedServices.length > 0 && (
        <section className="nst-light py-20 border-t border-[#D9E0F0]">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
              Related <span className="text-[#2E6BF6]">Services</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedServices.map((s) => (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group bg-white border border-[#E2E8F4] rounded-2xl p-6 card-lift hover:border-[#2E6BF6]/40"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#EDF1FB] flex items-center justify-center mb-4 group-hover:bg-[#2E6BF6] transition-colors">
                    <Icon name={s.icon} className="w-5 h-5 text-[#2E6BF6] group-hover:text-white transition-colors" />
                  </div>
                  <h3 className="text-lg font-bold text-[#0A1F47] group-hover:text-[#2E6BF6]">{s.title}</h3>
                  <p className="mt-2 text-sm text-[#0A1F47]/70">{s.short_desc}</p>
                  <div className="mt-4 text-[#2E6BF6] text-sm font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
                    Learn more <ArrowRight className="w-4 h-4" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Filtered articles */}
      {articles.length > 0 && (
        <section className="nst-light py-20 border-t border-[#D9E0F0]">
          <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-[#0A1F47]">
              {topic.name} <span className="text-[#2E6BF6]">Insights</span>
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {articles.map((a) => (
                <Link
                  key={a.slug}
                  to={`/insights/${a.slug}`}
                  className="group bg-white border border-[#E2E8F4] rounded-2xl p-6 card-lift hover:border-[#2E6BF6]/40"
                >
                  <span className="text-xs font-semibold text-[#2E6BF6] uppercase tracking-wider">{a.category}</span>
                  <h3 className="text-lg font-bold text-[#0A1F47] mt-2 group-hover:text-[#2E6BF6] transition-colors">{a.title}</h3>
                  <p className="mt-2 text-sm text-[#0A1F47]/70">{a.excerpt}</p>
                  <div className="mt-4 flex items-center gap-2 text-xs text-[#0A1F47]/60 font-medium">
                    <Clock className="w-3.5 h-3.5" /> {a.read_time}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="nst-navy text-white py-16">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <h3 className="text-2xl md:text-3xl font-extrabold max-w-xl">
            Building on {topic.name}? Let’s talk architecture.
          </h3>
          <Link to="/#contact" className="btn-navy" style={{ background: "#2E6BF6" }}>
            Schedule a Review <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
